import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { EditBtn, EditBtnIcon, ColorPickerIcon } from './EditBtn';
import { useEditorStore, EditorMediaFile } from '~/shell/Store';
import { useLocation } from 'react-router-dom';
import FilePickerV2 from '~/editor/filePicker/FilePicker_v2';
import ColorPicker from '~/editor/colorPicker/ColorPicker';

export const EditBg = ({
  divElRef,
  id,
  type,
  style,
  updateFileHandler,
  parentId,
  hsva,
  setHsva,
  updateColor
}: {
  divElRef: React.RefObject<HTMLDivElement>;
  id: string;
  type: string;
  style?: React.CSSProperties;
  updateFileHandler?: (body: {
    id: string;
    file: unknown;
    type: string;
    parentId?: string;
  }) => void;
  parentId?: string;
  hsva?: string;
  setHsva?: (color: string) => void;
  updateColor?: (color: string) => void;
}) => {
  const fileElRef = useRef<HTMLDivElement>(null);
  const btnElRef = useRef<HTMLButtonElement>(null);
  const { updateFile } = useEditorStore();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showFileUpload = queryParams.get('showFileUpload') === 'true';

  //it is for learner journey
  const [showFilePicker, setShowFilePicker] = useState(
    showFileUpload && type === 'video'
  );

  const [compStyle, setCompStyle] = useState({
    position: 'absolute',
    top: '80px',
    zIndex: 199,
    width: '28px',
    right: '',
    ...style
  });

  const updatePosition = useCallback(() => {
    if (divElRef.current) {
      const pos = divElRef.current.getBoundingClientRect();

      let left = 0;
      let top = 20;

      const isBgImage =
        divElRef.current?.classList.contains('bgImage') ||
        divElRef.current?.classList.contains('emptyBgImage');
      const isVideoContainer =
        divElRef.current?.classList.contains('video-container');

      const hasPositionedAncestor =
        btnElRef.current &&
        btnElRef.current.offsetParent !== null &&
        btnElRef.current.offsetParent !== document.body;

      if (hasPositionedAncestor) {
        setCompStyle(prevStyle => ({
          ...prevStyle,
          left: '',
          right: '20px',
          top: '20px',
          ...style
        }));
        return;
      }

      if (isBgImage) {
        left = pos.left + divElRef.current.offsetWidth - 50;
        top = Math.max(pos.top + 20, 80);
      } else if (isVideoContainer) {
        left = pos.right - 50;
        top = Math.max(pos.top + 20, 80);
      } else {
        left = pos.width - 50;
        top = 20;
      }

      setCompStyle(prevStyle => ({
        ...prevStyle,
        left: `${left}px`,
        right: '20px',
        top: `${top}px`,
        ...style
      }));
    }
  }, [divElRef, btnElRef]);

  const updateFileElement = (file: EditorMediaFile) => {
    if (updateFileHandler) {
      updateFileHandler({
        id: id,
        file: file,
        type: type == 'image' ? 'pictures' : 'videos',
        parentId: parentId
      });
    } else {
      updateFile(id, file, type == 'image' ? 'pictures' : 'videos', parentId);
    }
    setShowFilePicker(false);
  };

  useEffect(() => {
    updatePosition();

    if (divElRef?.current) {
      window.addEventListener('resize', updatePosition);
      updatePosition();

      const resizeObserver = new ResizeObserver(() => {
        updatePosition();
      });

      resizeObserver.observe(divElRef.current);

      return () => {
        window.removeEventListener('resize', updatePosition);
        resizeObserver.disconnect();
      };
    }
  }, [divElRef, type, updatePosition]);

  return (
    <>
      <EditBtn
        ref={btnElRef}
        onMouseUp={e => {
          e.preventDefault();
          e.stopPropagation();
          setShowFilePicker(!showFilePicker);
        }}
        style={compStyle as React.CSSProperties}
        className="edit-bg-btn"
      >
        {type == 'color' ? <ColorPickerIcon /> : <EditBtnIcon />}
      </EditBtn>
      {showFilePicker &&
        (type === 'color' && hsva !== undefined && setHsva && updateColor ? (
          <ColorPicker
            hsva={hsva}
            setHsva={setHsva}
            updateColor={(color: string) => {
              updateColor(color);
              setShowFilePicker(false);
            }}
            onClose={() => setShowFilePicker(false)}
          />
        ) : (
          createPortal(
            <FilePickerV2
              fileElRef={fileElRef}
              updateFile={updateFileElement}
              type={type as 'image' | 'video' | 'document'}
              onClose={() => setShowFilePicker(false)}
            />,
            document.body
          )
        ))}
    </>
  );
};
